import type {
  EditorState,
  TrackType,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { GroupItem } from "../../vendor/src/features/remotion-editor/editor/items/group/group-item-type";
import { resolveGroupGeometry } from "../../vendor/src/features/remotion-editor/editor/items/group/group-geometry";
import { generateRandomId } from "../../vendor/src/features/remotion-editor/editor/utils/generate-random-id";
import {preserveItemAnimationWindow} from './animation-window';

export function itemOwner(
  state: {
    undoableState: Pick<EditorState["undoableState"], "tracks" | "items">;
  },
  id: string,
  seen = new Set<string>(),
): { track: TrackType; parentId: string | null } | null {
  if (seen.has(id)) return null;
  const direct = state.undoableState.tracks.filter((track) =>
    track.items.includes(id),
  );
  if (direct.length === 1) return { track: direct[0]!, parentId: null };
  if (direct.length > 1) return null;
  const parents = Object.values(state.undoableState.items).filter(
    (item) => item.type === "group" && item.children.includes(id),
  );
  if (parents.length !== 1) return null;
  const parent = parents[0]!;
  const owner = itemOwner(state, parent.id, new Set(seen).add(id));
  return owner ? { track: owner.track, parentId: parent.id } : null;
}

export function editableTree(
  state: EditorState,
  id: string,
  seen = new Set<string>(),
): boolean {
  if (seen.has(id)) return false;
  const item = state.undoableState.items[id];
  const owner = itemOwner(state, id);
  if (!item || !owner || owner.track.locked) return false;
  return (
    item.type !== "group" ||
    ((item.children.length > 0 || item.preserveGroupFrame === true) &&
      item.children.every((child) =>
        editableTree(state, child, new Set(seen).add(id)),
      ))
  );
}

type Split = (params: {
  state: EditorState;
  idToSplit: string;
  framePosition: number;
}) => EditorState;

/** Trim a group's source windows, never speed-scale its children to fit a smaller box. */
export function trimGroup(
  state: EditorState,
  id: string,
  frame: number,
  keep: "left" | "right",
  split: Split,
): EditorState {
  const owner = itemOwner(state, id);
  if (!owner) return state;
  const beforeParent = owner.parentId
    ? state.undoableState.items[owner.parentId]
    : null;
  const beforeIds = new Set(
    beforeParent?.type === "group" ? beforeParent.children : owner.track.items,
  );
  const divided = splitGroup(state, id, frame, split);
  if (divided === state) return state;
  const parent = owner.parentId
    ? divided.undoableState.items[owner.parentId]
    : null;
  const peerIds =
    parent?.type === "group"
      ? parent.children
      : divided.undoableState.tracks.find(
          (track) => track.id === owner.track.id,
        )!.items;
  const pieces = peerIds
    .filter((key) => !beforeIds.has(key))
    .sort(
      (a, b) =>
        divided.undoableState.items[a]!.from -
        divided.undoableState.items[b]!.from,
    );
  if (pieces.length !== 2) return state;
  const retain = pieces[keep === "left" ? 0 : 1]!,
    discard = pieces[keep === "left" ? 1 : 0]!;
  const items = { ...divided.undoableState.items },
    removed = new Set<string>();
  const remove = (key: string) => {
    if (removed.has(key)) return;
    removed.add(key);
    const item = items[key];
    if (item?.type === "group") item.children.forEach(remove);
    delete items[key];
  };
  remove(discard);
  if (parent?.type === "group")
    items[parent.id] = {
      ...parent,
      children: parent.children.filter((key) => !removed.has(key)),
    };
  const transitions = { ...divided.undoableState.transitions };
  for (const [key, transition] of Object.entries(transitions))
    if (
      removed.has(transition.sourceItemId) ||
      removed.has(transition.targetItemId)
    )
      delete transitions[key];
  return {
    ...divided,
    selectedItems: [retain],
    activeGroupId: state.activeGroupId === id ? retain : state.activeGroupId,
    undoableState: {
      ...divided.undoableState,
      items,
      transitions,
      tracks: divided.undoableState.tracks.map((track) =>
        track.items.some((key) => removed.has(key))
          ? { ...track, items: track.items.filter((key) => !removed.has(key)) }
          : track,
      ),
    },
  };
}

/** Split a compound group into disjoint trees. Children keep absolute time and source windows. */
export function splitGroup(
  state: EditorState,
  id: string,
  frame: number,
  split: Split,
): EditorState {
  const original = state.undoableState.items[id];
  const owner = itemOwner(state, id);
  if (original?.type !== "group" || !owner || !editableTree(state, id))
    return state;
  const originalGeometry=resolveGroupGeometry(original,state.undoableState.items);
  if(frame<=originalGeometry.from||frame>=originalGeometry.from+originalGeometry.durationInFrames)return state;
  let next = state;
  for (const childId of original.children) {
    const child = next.undoableState.items[childId];
    if (!child) return state;
    if (child.from < frame && child.from + child.durationInFrames > frame) {
      const divided = split({
        state: next,
        idToSplit: childId,
        framePosition: frame,
      });
      if (divided === next) return state;
      next = divided;
    }
  }
  const current = next.undoableState.items[id];
  if (current?.type !== "group") return state;
  const leftIds = current.children.filter((childId) => {
    const child = next.undoableState.items[childId];
    return child && child.from + child.durationInFrames <= frame;
  });
  const rightIds = current.children.filter(
    (childId) => (next.undoableState.items[childId]?.from ?? -1) >= frame,
  );
  if (
    leftIds.length + rightIds.length !== current.children.length
  )
    return state;
  const used = new Set([
    ...Object.keys(next.undoableState.items),
    ...Object.keys(next.undoableState.assets),
    ...Object.keys(next.undoableState.transitions),
  ]);
  const allocate = () => {
    const seed = generateRandomId(12);
    let key = seed,
      n = 1;
    while (used.has(key)) key = seed + "-" + n++;
    used.add(key);
    return key;
  };
  const make = (children: string[],from:number,durationInFrames:number): GroupItem => {
    const geometry={...originalGeometry,from,durationInFrames};
    return {
      ...preserveItemAnimationWindow(original,state.undoableState.fps,geometry.from-originalGeometry.from,originalGeometry.durationInFrames),
      timelineSourceItemId: original.timelineSourceItemId ?? original.id,
      ...geometry,
      preserveGroupFrame:true,
      id: allocate(),
      children,
      positionKeyframes: original.positionKeyframes?.map((point) => ({
        ...point,
        frame: point.frame - (geometry.from - originalGeometry.from),
      })),
    };
  };
  const left = make(leftIds,originalGeometry.from,frame-originalGeometry.from),
    right = make(rightIds,frame,originalGeometry.from+originalGeometry.durationInFrames-frame);
  const items = {
    ...next.undoableState.items,
    [left.id]: left,
    [right.id]: right,
  };
  delete items[id];
  const parent = owner.parentId ? items[owner.parentId] : null;
  if (parent?.type === "group")
    items[parent.id] = {
      ...parent,
      children: parent.children.flatMap((child) =>
        child === id ? [left.id, right.id] : [child],
      ),
    };
  return {
    ...next,
    selectedItems: [right.id],
    activeGroupId: state.activeGroupId === id ? right.id : state.activeGroupId,
    undoableState: {
      ...next.undoableState,
      items,
      tracks: next.undoableState.tracks.map((track) =>
        track.items.includes(id)
          ? {
              ...track,
              items: track.items.flatMap((key) =>
                key === id ? [left.id, right.id] : [key],
              ),
            }
          : track,
      ),
      transitions: Object.fromEntries(
        Object.entries(next.undoableState.transitions).map(
          ([key, transition]) => [
            key,
            {
              ...transition,
              sourceItemId:
                transition.sourceItemId === id
                  ? right.id
                  : transition.sourceItemId,
              targetItemId:
                transition.targetItemId === id
                  ? left.id
                  : transition.targetItemId,
            },
          ],
        ),
      ),
    },
  };
}
